/**
 * SiteBrandCard — the site's accent colour and crest, in Site Appearance.
 *
 * The accent drives every gold detail on the public site (buttons, chips,
 * hero accent line, links) and the crest is the same file the header, footer
 * and app icon read.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MediaUpload } from '@/components/MediaUpload';
import { Loader2, RotateCcw, Save, Palette } from 'lucide-react';

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const SiteBrandCard = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-site-brand'],
    queryFn: async () => (await api.get('/admin/site-brand')).data,
  });

  const [accent, setAccent] = useState('');
  const [logo, setLogo] = useState('');
  useEffect(() => {
    if (!data) return;
    setAccent(data.accent_color || '');
    setLogo(data.logo_url || '');
  }, [data]);

  const valid = HEX.test(accent || '');
  const dirty = !!data && (accent !== (data.accent_color || '') || logo !== (data.logo_url || ''));

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-site-brand'] });
    queryClient.invalidateQueries({ queryKey: ['app-config'] });
    queryClient.invalidateQueries({ queryKey: ['public-site-brand'] });
  };

  const save = useMutation({
    mutationFn: async () => (await api.put('/admin/site-brand', {
      accent_color: accent, logo_url: logo,
    })).data,
    onSuccess: () => { toast.success('Brand saved — the site is wearing it now'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post('/admin/site-brand/reset')).data,
    onSuccess: (row) => {
      setAccent(row.accent_color || '');
      toast.success('Back to the colour the app ships with');
      refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <Skeleton className="h-72 w-full" data-testid="site-brand-loading" />;

  const preview = valid ? accent : (data?.default_accent || '#C9992B');

  return (
    <Card data-testid="site-brand-card">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Palette className="h-4 w-4 text-slate-500" />
          <CardTitle className="text-base">Brand colour &amp; crest</CardTitle>
          {data?.customised && (
            <Badge variant="secondary" className="text-[10px]" data-testid="site-brand-customised">
              Changed from the original
            </Badge>
          )}
        </div>
        <CardDescription>
          The accent colour every public page uses for buttons, chips, links and the hero accent
          line, and the crest shown in the header, the footer and the app icon.
          {data?.updated_at
            ? ` Last changed by ${data.updated_by} on ${new Date(data.updated_at).toLocaleDateString()}.`
            : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <Label className="text-xs">Accent colour</Label>
            <div className="flex flex-wrap gap-2" data-testid="site-brand-presets">
              {(data?.presets || []).map((p) => (
                <button
                  key={p.value}
                  type="button"
                  title={p.name}
                  onClick={() => setAccent(p.value)}
                  data-testid={`site-brand-preset-${p.value.replace('#', '')}`}
                  className={`h-9 w-9 rounded-full border-2 transition-transform hover:scale-110 ${
                    (accent || '').toUpperCase() === p.value.toUpperCase()
                      ? 'border-slate-900' : 'border-white shadow'
                  }`}
                  style={{ backgroundColor: p.value }}
                  aria-label={p.name}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={valid ? accent : preview}
                onChange={(e) => setAccent(e.target.value.toUpperCase())}
                className="h-10 w-12 cursor-pointer rounded border border-slate-200 bg-white p-1"
                data-testid="site-brand-colour-input"
                aria-label="Pick any colour"
              />
              <Input
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                placeholder="#C9992B"
                className="max-w-[160px] font-mono"
                data-testid="site-brand-hex-input"
              />
              {!valid && accent !== '' && (
                <span className="text-xs text-red-600" data-testid="site-brand-hex-error">
                  Use a hex colour like #C9992B
                </span>
              )}
            </div>
          </div>

          <MediaUpload
            value={logo}
            onChange={(url) => setLogo(url || '')}
            purpose="logo"
            label="Crest / logo"
            hint="A transparent PNG or SVG reads best. Used in the header, the footer and emails."
            testId="site-brand-logo"
            previewClassName="h-20"
          />
        </div>

        {/* Live preview of what the accent does */}
        <div
          className="rounded-xl border border-slate-200 bg-[#F6F1E4] p-4 space-y-3"
          data-testid="site-brand-preview"
        >
          <p className="text-[11px] uppercase tracking-wide text-slate-500">How it will look</p>
          <p className="text-xl font-serif text-[#201D17]">
            Everything the ministry runs,{' '}
            <span style={{ color: preview }} data-testid="site-brand-preview-accent">in one place.</span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="rounded-full px-4 py-2 text-xs font-semibold"
              style={{ backgroundColor: preview, color: data?.accent_ink || '#F7F1E3' }}
              data-testid="site-brand-preview-button"
            >
              Give now
            </span>
            <span
              className="rounded-full border px-3 py-1 text-[11px]"
              style={{ borderColor: preview, color: preview }}
            >
              What&apos;s on
            </span>
            <span className="h-1 w-16 rounded" style={{ backgroundColor: preview }} />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={!data?.customised || reset.isPending}
            onClick={() => reset.mutate()}
            data-testid="site-brand-reset"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Back to the original
          </Button>
          <Button
            size="sm"
            disabled={!dirty || !valid || save.isPending}
            onClick={() => save.mutate()}
            data-testid="site-brand-save"
          >
            {save.isPending
              ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              : <Save className="h-3.5 w-3.5 mr-1.5" />} Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SiteBrandCard;
